// @flow
import React, { useMemo, useCallback, useRef, useEffect, useLayoutEffect, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import momentPlugin from '@fullcalendar/moment';
import ptbrLang from '@fullcalendar/core/locales/pt-br';
import esLang from '@fullcalendar/core/locales/es';
import classNames from 'classnames';
import moment from 'moment';
import { get, find, map, forEach, filter, isEqual, set, findKey } from 'lodash';
import { Calendar as FC } from '@fullcalendar/core';
import type
{
  WorkHours,
} from '@types';
import { useIsMobile, Log, isPresent, useTranslationNs } from '@helpers/helpers';
import { handleFullCalendarTimerHover } from './utils';
import CalendarEvent, { getEventClassNames } from './CalendarEvent';
import './Calendar.scss';


type Props = {
  activeModal: ?string,
  calendarView: 'timeGridWeek' | 'timeGridDay',
  selectedDate: string,
  appointments: Object[],
  clinicDoctors: Object,
  onGetAppointments: Function,
  onShowAppointment: Function,
  onUpdateAppointment: Function,
  onNewAppointmentClick: Function,
  isLoading: boolean,
  workHours: WorkHours,
  loadingItems: { [id: number]: boolean },
  isFinatialEnabled: boolean,
  clinic: Object,
  user: Object,
  filter: Object,
  language: string,
};


const plugins = [interactionPlugin, momentPlugin, timeGridPlugin, dayGridPlugin];

const getNow = (clinic: any) => {
  const forceTimezone = get(clinic, ['forceTimezone'], null);
  return isPresent(forceTimezone) ? moment.utc().add(forceTimezone, 'hours').format() : moment().format();
};

/**
 * Important Notes:
 *
 * - When use startOf and endOf avois using week, prefer use of isoWeek
 */

/**
 * While an event is pressed, FullCalendar keeps the exact props it had at the press. Any new
 * option (a callback from a re-render, e.g. when a refresh lands) rebuilds its context mid-drag,
 * and the drop then counts as coming from another calendar: no eventDrop, so the move isn't
 * saved and the dragged copy stays next to the refreshed one.
 */
type HeldWhilePressedProps = {
  children: any,
  held: boolean, // eslint-disable-line react/no-unused-prop-types
};

const HeldWhilePressedView = ({ children }: HeldWhilePressedProps) => children;
const HeldWhilePressed = React.memo(HeldWhilePressedView, (previous, next) => next.held);

const Calendar = ({
  activeModal, isLoading, calendarView, clinicDoctors, isFinatialEnabled, language, loadingItems, onGetAppointments,
  onNewAppointmentClick, onShowAppointment, onUpdateAppointment, selectedDate, workHours, clinic, user, ...props
}: Props) => {
  const [_] = useTranslationNs('agenda');
  const api = useRef(null);
  const [isMobile] = useIsMobile();
  const [ghostAppointment, setGhostAppointment] = useState(null);
  const refHash = useRef({});
  const scrollTimeTriggered = useRef(false);
  const lastScrollTime = useRef(null);
  const lastAppointmentFetch = useRef(null);
  const [firstFetch, setFirstFetch] = useState(false);
  const holderRef = useRef(null);
  const [pressHold, setPressHold] = useState(null);

  /**
   * Only show appointments rendered in agenda
   */

  const isSameWeekThatSelectedDate = useCallback((date) => (
    date.isBetween(moment.utc(selectedDate).startOf('isoWeek'), moment.utc(selectedDate).endOf('isoWeek'))
      || date.isSame(moment.utc(selectedDate).startOf('isoWeek'), 'day')
      || date.isSame(moment.utc(selectedDate).endOf('isoWeek'), 'day')
  ), [selectedDate]);

  const appointments = useMemo(() => filter(props.appointments, appointment => {
    const start = moment.utc(moment.utc(appointment.start).format('YYYY-MM-DD HH:mm'));
    return isSameWeekThatSelectedDate(start);
  }), [props.appointments, isSameWeekThatSelectedDate]);

  /**
   * Keep FullCalendar unchanged while an event is pressed, to move or resize it, so a live refresh
   * (see AgendaLiveUpdates) can't lose the change. Two parts, both needed:
   * - its events: the inputs below are held, so the effects that replace them (new appointments,
   *   and the end of a load of days not on screen yet: isLoading turning false runs
   *   changeView/refetchEvents) wait for the release. A refresh of the days on screen (live, landing
   *   again, "Atualizar") doesn't touch isLoading: only its new appointments replace events.
   *   Otherwise a resize ends on an event without data (TypeError reading 'extendedProps');
   * - its options: HeldWhilePressed hands it the same element. Otherwise a move's drop counts as
   *   coming from another calendar: no eventDrop, the move isn't saved, and the card shows twice.
   */
  const liveCalendarInputs = { appointments, ghostAppointment, loadingItems, isLoading };
  const latestCalendarInputs = useRef(liveCalendarInputs);
  useLayoutEffect(() => {
    latestCalendarInputs.current = liveCalendarInputs;
  });
  const {
    appointments: calendarAppointments,
    ghostAppointment: calendarGhostAppointment,
    loadingItems: calendarLoadingItems,
    isLoading: calendarIsLoading,
  } = pressHold || liveCalendarInputs;

  useEffect(() => {
    const holder = holderRef.current;
    if (!holder) return undefined;

    let releaseTimer;
    let removeReleaseListeners = () => {};

    const release = () => {
      removeReleaseListeners();
      // Two ticks later, once FullCalendar has handled this same mouseup/touchend and saved the
      // move or resize: on touch it listens after us and ends the drag on a timer of its own
      releaseTimer = setTimeout(() => {
        releaseTimer = setTimeout(() => setPressHold(null), 0);
      }, 0);
    };

    // A mouseup can get lost (e.g. to a context menu): moving with no button down means released
    const releaseIfNoButton = (event) => {
      if (event.buttons === 0) release();
    };

    const hold = (event) => {
      const { target } = event;
      if (!target || typeof target.closest !== 'function' || !target.closest('.fc-event:not(.fc-bg-event)')) return;
      // Like FullCalendar, which only drags with the primary button and without ctrl
      if (event.type === 'mousedown' && (event.button !== 0 || event.ctrlKey)) return;

      clearTimeout(releaseTimer);
      removeReleaseListeners();
      // With a release still pending, FullCalendar shows the held events: keep holding those
      setPressHold(current => current || latestCalendarInputs.current);

      // Like FullCalendar: a mouse drag ends on mouseup anywhere, a touch one on the touched
      // element, which may have left the DOM by then. A native drag of selected text ends with
      // dragend instead, and no mouseup
      const isTouch = event.type === 'touchstart';
      const releaseTarget = isTouch ? target : window;
      const releaseEvents = isTouch ? ['touchend', 'touchcancel'] : ['mouseup', 'dragend'];
      releaseEvents.forEach(type => releaseTarget.addEventListener(type, release));
      window.addEventListener('blur', release);
      if (!isTouch) window.addEventListener('mousemove', releaseIfNoButton);
      removeReleaseListeners = () => {
        releaseEvents.forEach(type => releaseTarget.removeEventListener(type, release));
        window.removeEventListener('blur', release);
        window.removeEventListener('mousemove', releaseIfNoButton);
        removeReleaseListeners = () => {};
      };
    };

    // Capture phase: before FullCalendar's own mousedown/touchstart handlers see the press
    holder.addEventListener('mousedown', hold, true);
    holder.addEventListener('touchstart', hold, { capture: true, passive: true });

    return () => {
      holder.removeEventListener('mousedown', hold, true);
      holder.removeEventListener('touchstart', hold, { capture: true, passive: true });
      removeReleaseListeners();
      clearTimeout(releaseTimer);
    };
  }, []);

  const eventAllow = useCallback((dropLocation: Object, event: Object) => !get(loadingItems, String(get(event, 'id', '')), false), [loadingItems]);

  const clinicNow = useMemo(() => getNow(clinic), [clinic]);

  const scrollTime = useMemo(() => get(clinic, 'businessHoursStart') || '9:00', [clinic]);
  const snapDuration = useMemo(() => get(clinic, 'snapDuration') || '00:15:00', [clinic]);
  const defaultDate = useMemo(() => moment('12', 'HH').toDate(), []);
  const locale = useMemo(() => (language === 'es' ? esLang : ptbrLang), [language]);
  const defaultView = useMemo(() => (isMobile || calendarView === 'timeGridDay' ? 'timeGridDay' : 'timeGridWeek'), [calendarView, isMobile]);

  // Days hidden in the week view (0 = Sunday, 6 = Saturday). In day view nothing is hidden
  // so the selected day always renders, regardless of the clinic's weekend settings.
  const hiddenDays = useMemo(() => {
    if (calendarView === 'timeGridDay') return [];
    const days = [];
    if (!get(clinic, 'showSaturday', false)) days.push(6);
    if (!get(clinic, 'showSunday', false)) days.push(0);
    return days;
  }, [calendarView, clinic]);

  // ============================================================================================================================
  // ========================================================= Utilities ================================================
  // ============================================================================================================================

  /**
   * Order event by doctor id
   */
  const eventOrder = useCallback((a, b) => {
    const eventA = find(appointments, ev => String(get(ev, 'id', '')) === String(get(a, 'id', '')));
    const eventB = find(appointments, ev => String(get(ev, 'id', '')) === String(get(b, 'id', '')));
    return parseInt(get(eventA, 'doctorId', 0), 10) - parseInt(get(eventB, 'doctorId', 0), 10);
  }, [appointments]);

  const defaultDuration = get(clinicDoctors[(
    (get(props.filter, ['doctor', 'length']) ? get(props.filter, ['doctor', String(0)]) : null) ||
    (!get(user, ['roleType']) === 'Doctor' && !get(user, ['advancedData', 'doctorId'])) ||
    get(props.filter, ['doctor', String(1)]) ||
    findKey(clinicDoctors)
  )], 'defaultAppointmentDuration');

  /**
   * Calendar vies settings
   */
  const viewsConfig = useMemo(() => ({
    week: {
      dayHeaderFormat: 'ddd, D/M',
      titleFormat: 'DD/MMM',
    },
    day: {
      dayHeaderFormat: 'dddd',
      titleFormat: 'ddd, DD/MM',
    },
  }), []);
  /**
   * Get the appointment by event id, used to merge with event extendedProps
   */
  const getAppointment = useCallback((id: string = '') => ({
    id,
    ...(find(appointments, ap => parseInt(get(ap, 'id', ''), 10) === parseInt(id, 10)) || {}),
  }), [appointments]);

  /**
   * get Business work hours setting
   */
  const businessHours = useMemo(() => ({
    startTime: get(clinic, 'businessHoursStart') || '9:00',
    endTime: get(clinic, 'businessHoursEnd') || '18:00',
    daysOfWeek: [1, 2, 3, 4, 5],
  }), [clinic]);

  // ============================================================================================================================
  // ========================================================= FC Events sources ================================================
  // ============================================================================================================================

  useEffect(() => {
    if (!firstFetch) {
      const start = isMobile ? moment.utc().startOf('day') : moment.utc().startOf('isoWeek').subtract(1, 'day');
      const end = isMobile ? moment.utc().endOf('day') : moment.utc().endOf('isoWeek').add(1, 'day');
      onGetAppointments(isMobile || calendarView === 'timeGridDay');
      refHash.current = JSON.stringify({ selectedDate, defaultDate, isMobile });
      Log.info('onGetAppointments', start, end);
      setFirstFetch(true);
    }
  }, [calendarView, defaultDate, firstFetch, isMobile, onGetAppointments, selectedDate]);

  /**
   * Fetch appoointments when selectedDate or defaultDate ou mobile changes
   */
  const fetchSource = useCallback(({ start, end, timezone }, callback: Function) => {
    if (!isEqual(refHash.current, JSON.stringify({ selectedDate, defaultDate, isMobile }))) {
      onGetAppointments(isMobile || calendarView === 'timeGridDay');
      refHash.current = JSON.stringify({ selectedDate, defaultDate, isMobile });
      Log.info('onGetAppointments', start, end, timezone);
    }
    callback([]);
  }, [calendarView, defaultDate, isMobile, onGetAppointments, selectedDate]);

  /**
   * Get appointments and handle data
   */
  const appointmentSource = useCallback((dt, callback: Function) => {
    const appointmentsList = map<any, any>(calendarAppointments, appointment => {
      const canChange = !get(calendarLoadingItems, get(appointment, 'id'), false) && String(appointment.doctorId) !== '1';

      return {
        ...appointment,
        startEditable: canChange,
        durationEditable: canChange,
        doctorId: appointment.doctorId,
      };
    });
    const renderAppointments = calendarGhostAppointment ? [...appointmentsList, calendarGhostAppointment] : appointmentsList;
    callback(renderAppointments);
  }, [calendarAppointments, calendarGhostAppointment, calendarLoadingItems]);

  /**
   * Render doctor workhours background event
   */
  const workHoursSource = useCallback((dt, callback: Function) => {
    const doctorFilterId = get(props.filter, ['doctor', '0']);
    const renderedDays = (defaultView === 'timeGridWeek') ? [1, 2, 3, 4, 5, 6, 7] : [moment(selectedDate).day() || 0];
    if (doctorFilterId > 0) {
      const doctorWorkHours: Object = workHours || {};

      const doctorWorkHourEvents = [];

      forEach(renderedDays, (day) => {
        const dayWorkHourEvents = filter(doctorWorkHours, item => get(item, 'weekday') === day);


        forEach(dayWorkHourEvents, dayWorkHourEvent => {
          if (!dayWorkHourEvent) return;

          const dateStr = moment.utc(selectedDate).startOf('isoWeek').weekday(day).format('YYYY-MM-DD');

          const start = moment.utc(`${dateStr} ${dayWorkHourEvent.start}:00`);
          const end = moment.utc(`${dateStr} ${dayWorkHourEvent.end}:00`);

          const id = doctorFilterId + '-' + moment(dateStr, 'DD/MM/YYYY').unix() + '-';

          const wkEvent = {
            id,
            doctorId: doctorFilterId,
            start: start.toISOString(),
            end: end.toISOString(),
            display: 'background',
            classNames: ['bg-doctor-workhour'],
            backgroundColor: 'rgb(143, 223, 130)',
            isBackgroundEvent: true,
          };

          doctorWorkHourEvents.push(wkEvent);
        });
      });
      callback(filter(doctorWorkHourEvents, item => item !== null));
    }
  }, [defaultView, props.filter, selectedDate, workHours]);

  // ============================================================================================================================
  // ========================================================= Rendering related ================================================
  // ============================================================================================================================

  /**
   * Helper to merge event data for rendering
   */
  const getMergedEventData = useCallback((eventApi) => ({
    ...eventApi,
    ...eventApi.extendedProps,
    ...getAppointment(get(eventApi, 'id', '')),
  }), [getAppointment]);

  /*
   * Handle event content rendering (React Node)
   */
  const renderEventContent = useCallback((eventInfo) => {
    // If it's a background event, we let FullCalendar handle the rendering (by returning null/undefined)
    // or we can return specific content. For work hours (background), we usually want empty.
    if (eventInfo.event.display === 'background') {
      return null;
    }

    const eventData = getMergedEventData(eventInfo.event);

    return (
      <CalendarEvent
        event={eventData}
        isFinatialEnabled={isFinatialEnabled}
        loadingItems={loadingItems}
        clinicDoctors={clinicDoctors}
        agendaFilter={props.filter}
        isMobile={isMobile || window.isCordovaApp}
        classList="" // Classes are handled by eventClassNames
        clinic={clinic}
        user={user}
        snapDuration={snapDuration}
        _={_}
      />
    );
  }, [getMergedEventData, isFinatialEnabled, loadingItems, clinicDoctors, props.filter, isMobile, clinic, user, snapDuration, _]);

  /*
   * Handle event class names (Hoist classes to FC wrapper)
   */
  const renderEventClassNames = useCallback((eventInfo) => {
    const eventData = getMergedEventData(eventInfo.event);

    return getEventClassNames({
      event: eventData,
      clinicDoctors,
      agendaFilter: props.filter,
      user,
      snapDuration,
    });
  }, [getMergedEventData, clinicDoctors, props.filter, user, snapDuration]);

  // =============================================================================================================================
  // ========================================================= Effects ===========================================================
  // =============================================================================================================================
  /**
   * Handle time hover
   */
  useEffect(() => {
    try {
      if (!isMobile && api.current) {
        handleFullCalendarTimerHover(snapDuration);
      }
    } catch (e) {
      Log.error(e);
    }
  }, [isMobile, snapDuration]);

  /**
   * Unselect when modal is closed
   */
  useEffect(() => {
    try {
      if (activeModal !== 'NewAppointmentModal' && api.current) {
        api.current.unselect();
      }
    } catch (e) {
      Log.error(e);
    }
  }, [activeModal]);

  /**
   * Handle scrollTime
   * First scroll
   */
  useEffect(() => {
    try {
      if (!scrollTimeTriggered.current && api.current && !isLoading) {
        scrollTimeTriggered.current = true;
        const _api: FC = api.current;
        setTimeout(() => {
          try {
            _api.scrollToTime(`${scrollTime}`);
          } catch (e) {
            //
          }
        }, 200);
        Log.info(`[Agenda]: Scrolled to ${scrollTime}`);
      }
    } catch (e) {
      // Log.error(e);
    }
  }, [scrollTime, isLoading]);

  /**
   * Refetch events when ghostAppointment or calendar changes.
   * This and the other effects that update FullCalendar from the held inputs run as layout
   * effects, inside the commit: as passive effects, a press landing right after a refresh's
   * render would run them under the press (the hold's own render flushes them first), and
   * replace the pressed event before FullCalendar starts dragging it.
   */
  useLayoutEffect(() => {
    try {
      if (api.current) {
        api.current.refetchEvents();
      }
    } catch (e) {
      Log.error(e);
    }
  }, [calendarGhostAppointment]);

  const lcasCalcView = useRef(null);

  useEffect(() => {
    try {
      const calcView = isMobile || calendarView === 'timeGridDay';
      if (calcView !== lcasCalcView.current) {
        lcasCalcView.current = calcView;
        onGetAppointments(calcView);
      }
    } catch (e) {
      Log.error(e);
    }
  }, [calendarView, isMobile, onGetAppointments]);

  // =============================================================================================================================
  // ========================================================= Agenda Events Handlers ============================================
  // =============================================================================================================================

  /**
  * Handle on click in cell of calendar
  */
  const handleDateClick = useCallback((params: Object) => {
    if (isMobile || window.isCordovaApp) {
      const start = moment.utc(params.date);
      let startT = moment(start);
      let endT = moment(start);

      if (moment(start).minutes() >= 30) {
        endT = moment(start).set('minutes', 30).add(defaultDuration, 'minutes');
      } else {
        endT = moment(start).set('minutes', 0).add(defaultDuration, 'minutes');
      }

      if (startT.minutes() >= 30) {
        startT = startT.clone().set('minutes', 30);
      } else {
        startT = startT.clone().set('minutes', 0);
      }

      const _ghostAppointment = {
        start: startT.toISOString(),
        end: endT.toISOString(),
        allDay: false,
        title: '',
        extendedProps: {
          full: false,
          time: moment.utc(start).toISOString(),
          date: moment.utc(start).format('YYYY-MM-DD'),
          badge: null,
          patient: null,
          restricted: false,
          isGhost: true,
          duration: defaultDuration,
          room: 1,
          doctorId: 72,
          notes: '',
        },
      };
      setGhostAppointment(_ghostAppointment);
    }
  }, [defaultDuration, isMobile]);

  /**
   * Handle select interval on calendar
   */
  const handleSelect = useCallback(({ startStr, endStr }: Object) => {
    if (isMobile) return;
    let end = moment.utc(endStr);
    const start = moment.utc(startStr);

    const isDayClick = moment.utc(end).diff(start, 'minutes') === moment(`${moment().format('YYYY-MM-DD')} ${snapDuration}`).minute();

    if (isDayClick) {
      end = start.clone().add(defaultDuration, 'minutes');
    }

    onNewAppointmentClick(
      start,
      end,
      isDayClick,
    );
  }, [defaultDuration, isMobile, onNewAppointmentClick, snapDuration]);

  /**
   * The latest copy of the appointment a move or resize saves. While a press is held,
   * FullCalendar's event and these callbacks are from the render at the press, so saving the
   * event's data would send back, as it was, anything a colleague changed during the drag.
   * There is none when the appointment left the agenda since the press (deleted, or moved out of
   * view by a colleague): saving the press-time copy would undo that, so the drop is reverted.
   * Ghost appointments have none either, and were never saved by a drop.
   */
  const getLatestAppointment = useCallback((event: Object) => {
    const id = parseInt(get(event, 'id', ''), 10);
    return find(latestCalendarInputs.current.appointments, ap => parseInt(get(ap, 'id', ''), 10) === id);
  }, []);

  /**
   * Handle event drop
   */
  const handleEventDrop = useCallback(({ event, revert }: Object) => {
    if (isMobile) return;

    const latest = getLatestAppointment(event);
    if (!latest) {
      revert();
      return;
    }
    let eData = { ...latest };
    eData = set(eData, 'date', event.start.toISOString());
    eData = set(eData, 'time', event.start.toISOString());
    onUpdateAppointment(eData);
  }, [onUpdateAppointment, isMobile, getLatestAppointment]);

  /**
   * Handle event risize
   */
  const handleEventResize = useCallback(({ event, revert }: Object) => {
    if (isMobile) return;

    const latest = getLatestAppointment(event);
    if (!latest) {
      revert();
      return;
    }
    const duration = moment.duration(moment(event.end).diff(event.start));
    let eData = { ...latest };
    eData = set(eData, 'date', event.start.toISOString());
    eData = set(eData, 'time', event.start.toISOString());
    eData = set(eData, 'duration', duration.asMinutes());
    onUpdateAppointment(eData);
  }, [onUpdateAppointment, isMobile, getLatestAppointment]);

  /**
   * Handle event click
   */
  const handleEventClick = useCallback((dt) => {
    const event = { ...dt.event.extendedProps, ...getAppointment(get(dt, ['event', 'id'], '')) };

    const highPrivacyBlocked = get(event, 'restricted', false);
    if (event.restricted || highPrivacyBlocked) return false;

    if (event.isGhost) {
      const getTime = () => {
        if (isMobile) {
          const minutes = moment.utc(event.time).minutes();

          const eventTime = moment.utc(event.time).set('minutes', minutes >= 30 ? 30 : 0);

          return eventTime;
        }

        return moment.utc(event.time);
      };

      onNewAppointmentClick(getTime().format('YYYY-MM-DD HH:mm:ss'),
        getTime().add(defaultDuration, 'minutes').format('YYYY-MM-DD HH:mm:ss'));
      setGhostAppointment(null);
    } else {
      onShowAppointment(event);
    }
  }, [getAppointment, onNewAppointmentClick, defaultDuration, isMobile, onShowAppointment]);

  useLayoutEffect(() => {
    try {
      if (api.current && !calendarIsLoading) {
        const _api: FC = api.current;
        if (!isMobile) {
          _api.gotoDate(moment.utc(selectedDate).startOf('isoWeek').toDate());
        } else {
          _api.gotoDate(moment.utc(selectedDate).toDate());
        }
      }
    } catch (e) {
      Log.error(e);
    }
  }, [selectedDate, calendarIsLoading, isMobile]);

  useLayoutEffect(() => {
    try {
      if (api.current) {
        const _api: FC = api.current;
        _api.gotoDate(selectedDate);
      }
    } catch (e) {
      Log.error(e);
    }
  }, [calendarIsLoading, calendarView, selectedDate, isMobile]);


  useLayoutEffect(() => {
    try {
      if (api.current && !calendarIsLoading) {
        const _api: FC = api.current;
        if (calendarView === 'timeGridDay' || isMobile) {
          _api.changeView(calendarView, selectedDate);
        } else {
          _api.changeView(calendarView, {
            start: moment.utc(selectedDate).startOf('isoWeek'),
            end: moment.utc(selectedDate).endOf('isoWeek'),
          });
        }
        _api.refetchEvents();
      }
    } catch (e) {
      Log.error(e);
    }
  }, [calendarIsLoading, calendarView, selectedDate, isMobile]);

  // useEffect(() => {
  //   try {
  //     if ((!scrollTimeTriggered.current || lastSelectedDate.current !== selectedDate) && api.current && !isLoading) {
  //       const _api: FC = api.current;
  //       setTimeout(() => _api.scrollToTime(`${scrollTime}`), 200);
  //       Log.info(`[Agenda]: Scrolled to ${scrollTime}`);
  //       scrollTimeTriggered.current = true;
  //       lastSelectedDate.current = selectedDate;
  //     }
  //   } catch (e) {
  //     Log.error(e);
  //   }
  // }, [scrollTime, isLoading, selectedDate]);

  useEffect(() => {
    try {
      if (api.current && !isLoading && lastScrollTime.current !== scrollTime) {
        const _api: FC = api.current;
        _api.setOption('scrollTime', scrollTime);
        lastScrollTime.current = scrollTime;
      }
    } catch (e) {
      Log.error(e);
    }
  }, [isLoading, scrollTime]);

  useLayoutEffect(() => {
    try {
      if (api.current && !calendarIsLoading && !isEqual(lastAppointmentFetch.current, calendarAppointments)) {
        const _api: FC = api.current;
        _api.removeAllEvents();
        _api.refetchEvents();
        lastAppointmentFetch.current = calendarAppointments;
      }
    } catch (e) {
      Log.error(e);
    }
  }, [calendarIsLoading, calendarAppointments, calendarLoadingItems]);

  const nowIndicator = useMemo(() => get(clinic, ['nowIndicator'], true), [clinic]);
  // FullCalendar compares options by identity: a new array on every render rebuilds it each time
  const eventSources = useMemo(() => [fetchSource, appointmentSource, workHoursSource], [fetchSource, appointmentSource, workHoursSource]);

  return (
    <div id="agenda-calendar">
      <section className="agenda">
        <div ref={holderRef} className={classNames('calendar-holder', { isMobile })}>
          <HeldWhilePressed held={Boolean(pressHold)}>
            <FullCalendar
              allDaySlot={false}
              firstDay={1}
              slotDuration="00:30:00"
              scrollTime={scrollTime}
              scrollTimeReset={false}
              snapDuration={snapDuration}
              slotLabelFormat="HH:mm"
              eventTimeFormat="HH:mm"
              header={false}
              nowIndicator={nowIndicator}
              unselectAuto={false}
              eventOverlap
              selectable={!isMobile && !window.isCordovaApp}
              editable
              eventStartEditable
              eventDurationEditable
              slotEventOverlap={false}
              eventMinHeight={0}
              eventClassNames={renderEventClassNames}
              initialDate={defaultDate}
              displayEventEnd={false}
              displayEventTime={false}
              hiddenDays={hiddenDays}
              timeZoneParam="UTC"
              timeZone={false}
              locale={locale}
              height="100%"
              eventAllow={eventAllow}
              eventOrder={eventOrder}
              initialView={defaultView}
              now={clinicNow}
              views={viewsConfig}
              eventSources={eventSources}
              businessHours={businessHours}
              plugins={plugins}
              eventContent={renderEventContent}
              select={handleSelect}
              eventDrop={handleEventDrop}
              eventResize={handleEventResize}
              dateClick={handleDateClick}
              eventClick={handleEventClick}
              selectMirror
              ref={ref => { api.current = ref && ref.getApi(); }}
            />
          </HeldWhilePressed>
        </div>
      </section>
    </div>

  );
};

// $FlowFixMe
export default React.memo(Calendar, isEqual);
